import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  defaultNavigationState,
  NavigationState,
  TabStateChangeSubscriber,
} from './types';
import { NavLink } from '@lmt/unicorn-shared/app/components/navigation/types';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';

export const navigationSlice = createSlice({
  name: 'settings',
  initialState: defaultNavigationState,
  reducers: {
    tabsLoaded(state, action: PayloadAction<NavLink[]>) {
      state.tabs = action.payload;
    },
    tabsRemovedAll(state) {
      state.tabs = state.tabs.filter((tab) => tab.hasUnsavedChanges);
    },
    tabCreated(state, action: PayloadAction<NavLink>) {
      state.tabs.push(action.payload);
    },
    tabRemoved(state, action: PayloadAction<string>) {
      state.tabs = state.tabs.filter((link) => link.id !== action.payload);
    },
    patientRemoved(state, action: PayloadAction<number>) {
      state.tabs = state.tabs.filter(
        (tab) =>
          !tab.path.includes(
            pages.patientProfile.link({ patientId: action.payload }),
          ),
      );
    },
    patientChanged(
      state,
      action: PayloadAction<{
        patientId: number;
        title: string;
      }>,
    ) {
      state.tabs = state.tabs.map((tab) =>
        tab.path.includes(
          pages.patientProfile.link({ patientId: action.payload.patientId }),
        )
          ? { ...tab, title: action.payload.title }
          : tab,
      );
    },
    tabTitleChanged(
      state,
      action: PayloadAction<{
        id: string;
        title: string;
      }>,
    ) {
      const tab = state.tabs.find((tab) => tab.id === action.payload.id);
      if (tab) {
        tab.title = action.payload.title;
      }
    },
    tabActivated(state, action: PayloadAction<{ id: string }>) {
      state.lastActiveTabId = action.payload.id;
    },
    pathChanged(
      state,
      action: PayloadAction<{
        id: string;
        path: string;
        title?: string;
        search?: string;
      }>,
    ) {
      state.tabs = state.tabs.map((tab) =>
        tab.id === action.payload.id
          ? {
              ...tab,
              path: action.payload.path,
              title: action.payload.title || tab.title,
              search: action.payload.search || tab.search,
            }
          : tab,
      );
    },
    queryStringChanged(
      state,
      action: PayloadAction<{
        id: string;
        search?: string;
        path?: string;
        title?: string;
      }>,
    ) {
      state.tabs = state.tabs.map((tab) =>
        tab.id === action.payload.id
          ? {
              ...tab,
              title: action.payload.title ?? tab.title,
              path: action.payload.path ?? tab.path,
              search: action.payload.search ?? tab.search,
            }
          : tab,
      );
    },
    defaultTabQueryStringChanged(
      state,
      action: PayloadAction<{
        title: string;
        search?: string;
      }>,
    ) {
      state.defaultTabs = state.defaultTabs.map((tab) =>
        tab.title === action.payload.title
          ? {
              ...tab,
              search: action.payload.search ?? tab.search,
            }
          : tab,
      );
      return state;
    },
    subscribeToTabStateChanges(
      state,
      action: PayloadAction<TabStateChangeSubscriber>,
    ) {
      state.tabStateChangeSubscribers.push(action.payload);
    },
    unsubscribeFromTabStateChanges(
      state,
      action: PayloadAction<TabStateChangeSubscriber>,
    ) {
      state.tabStateChangeSubscribers = state.tabStateChangeSubscribers.filter(
        (subscriber) => subscriber !== action.payload,
      );
    },
    tabHasUnsavedChangesChanged(
      state,
      action: PayloadAction<{
        id: string;
        hasUnsavedChanges?: boolean;
      }>,
    ) {
      const tab = state.tabs.find((tab) => tab.id === action.payload.id);
      if (tab) {
        tab.hasUnsavedChanges = action.payload.hasUnsavedChanges;
      }
    },
    setHandler(
      state,
      action: PayloadAction<{
        tabId: string;
        deactivateHandler?: () => unknown;
        activateHandler?: () => unknown;
      }>,
    ) {
      const tab = findTab(state, action.payload.tabId);
      if (tab) {
        tab.deactivateHandler = action.payload.deactivateHandler;
        tab.activateHandler = action.payload.activateHandler;
      }
    },
  },
});
export function findTab(state: NavigationState, tabId: string) {
  const tab =
    state.tabs.find((tab) => tab.id === tabId) ??
    state.defaultTabs.find((tab) => tab.id === tabId);
  return tab;
}

export const NavigationActions = navigationSlice.actions;
